import { CLIENT_TIER_META, CLIENT_TIERS, demoTierLabel } from '@repo/types'
/**
 * VRG-311 — shared constants for the acquisition demo console, used by both the
 * list page (`pages/internal/InternalDemosPage`) and the create flow modal
 * (`components/feedback/modals/CreateDemoModal`). Kept out of the page directory
 * so a components/-layer modal doesn't reach into pages/-layer for it.
 */

/**
 * Tiers 1-5 (user, 2026-08-21). Tier 1 is the highest-investment send — a real
 * product plus an encoded NFC card — and the number rises as the spend falls.
 *
 * `long_tail` is retained as a LABEL ONLY, not an option: it was the original
 * second tier and may still exist on historical rows, so the list must render it
 * rather than showing a raw key. It is deliberately absent from
 * DEMO_TIER_OPTIONS, so nothing new can be created with it.
 */
export const DEMO_TIER_LABELS: Record<string, string> = {
  tier_1: 'Tier 1',
  tier_2: 'Tier 2',
  tier_3: 'Tier 3',
  tier_4: 'Tier 4',
  tier_5: 'Tier 5',
  long_tail: 'Long tail (legacy)',
}

export const DEMO_LIFETIME_OPTIONS = [
  { label: 'Always', value: 'always' },
  { label: '14 days', value: '14' },
  { label: '30 days', value: '30' },
  { label: '90 days', value: '90' },
] as const

export type DemoLifetimeValue = (typeof DEMO_LIFETIME_OPTIONS)[number]['value']

/** Null = "Always" (never expires) — VRG-311 AC5's default, matching the API contract. */
export const lifetimeToExpiresAt = (lifetime: DemoLifetimeValue): string | undefined => {
  if (lifetime === 'always') return undefined
  const days = Number(lifetime)
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString()
}

/**
 * A demo sitting unopened this long is exactly the accumulation VRG-311 AC4 exists
 * to surface. Two weeks: long enough that a slow-to-open prospect isn't flagged the
 * day after sending, short enough that a demo sent ~20x/week doesn't silently age
 * for a quarter.
 */
export const STALE_DEMO_AGE_DAYS = 14

/**
 * Prospect tiers. **The tier describes the BRAND, not the send.**
 *
 * ── What actually defines a tier ────────────────────────────────────────────
 *
 * ANNUAL PRODUCTION VOLUME — units made per year. That is the revenue driver,
 * because a Vera certificate is issued per unit, so production volume IS our
 * addressable certificate count for that brand.
 *
 * EMPLOYEE COUNT IS ONLY A PROXY. It is here because it is publicly available
 * and production volume usually is not, so it is what an AE can actually look up
 * at the moment they pick a tier.
 *
 * ⚠️ CONSEQUENCE: when the real production number becomes known — said on a
 * call, or reported by an integration — that number WINS over the headcount
 * proxy. A 60-person house producing 400K units a year is a Tier 2 prospect
 * wearing Tier 4 clothes, and tiering by headcount would systematically
 * under-invest in exactly the brands worth the most. Re-tier on the real number.
 *
 *   Tier   Employees (proxy)          Units/yr   ~ARR each   Brands
 *   1      5K+                        1,000,000  $35M        ~218
 *   2      1K–5K                        100,000  $4M         ~589
 *   3      100–1K                     10,000  $560K       ~5,800
 *   4      20–100                      5,000  $335K       ~20,500
 *   5      under 20                      250  —           —   (artists, creators: wide range)
 *
 * TIERS ARE SIZE AND PRODUCTION ONLY (Spencer, 2026-10-16, VRG-715). Tiers 3–5
 * used to be defined as "on Shopify" brands; that qualifier is gone from the
 * labels and from the tier figures. How a brand sells is not part of its tier.
 *
 * Tier 2's volume was originally transcribed as 10K — identical to Tier 3 while
 * showing 7x its ARR, i.e. $400/unit against $56. Confirmed as 100,000, which
 * puts it at $40/unit between Tier 1 ($35) and Tier 3 ($56).
 *
 * TODO(VRG-311): store estimated annual production on the record rather than
 * leaving it implied by the tier, so it can be corrected when the real number
 * arrives without silently re-tiering. Belongs on `companies` now that demos are
 * a pipeline stage of the same entity.
 */
// VRG-701 — the figures live ONCE in @repo/types gtm/client-tiers.ts (the sales company's Tier
// field reads the same ones); this list derives its labels, unchanged, from there.
export const DEMO_TIER_OPTIONS = CLIENT_TIERS.map((t) => ({ label: demoTierLabel(t), value: t, ...(CLIENT_TIER_META[t].note ? { description: CLIENT_TIER_META[t].note } : {}) }))

export type DemoSendTier = (typeof DEMO_TIER_OPTIONS)[number]['value']
